import { useMemo } from 'react';
import api from 'services/api';
import {
  PendingCryptoWithdrawOrderDto,
  PendingFiatWithdrawOrderDto,
  WithdrawCryptoApi,
  WithdrawFiatApi,
} from 'services/generated';
import { handleErrors } from 'services/helpers';
import { rootApi } from 'store/root-api';

const withdrawFiatClient = new WithdrawFiatApi(undefined, undefined, api);
const withdrawCryptoClient = new WithdrawCryptoApi(undefined, undefined, api);

/**
 * A pending withdrawal order, normalised across the fiat and crypto endpoints.
 * This is the single source of truth for "does this product have a withdrawal
 * in flight" — both the portfolio reserved modal and the withdraw product list
 * read from here so they can never disagree.
 */
export type PendingWithdrawal = {
  productCode: string;
  productName: string;
  orderId: string;
  orderReference: string;
  orderAmount: number;
  orderAmountInDisplayCurrency?: number;
  amountAvailable: number;
  isCrypto: boolean;
};

const toPendingWithdrawal = (
  order: PendingFiatWithdrawOrderDto | PendingCryptoWithdrawOrderDto,
  isCrypto: boolean,
): PendingWithdrawal => ({
  productCode: order.productCode,
  productName: order.productName,
  orderId: order.orderId,
  orderReference: order.orderReference,
  orderAmount: order.orderAmount,
  orderAmountInDisplayCurrency: (order as PendingCryptoWithdrawOrderDto)
    .orderAmountInDisplayCurrency,
  amountAvailable: order.amountAvailable,
  isCrypto,
});

const withdrawalsApi = rootApi.injectEndpoints({
  endpoints: builder => ({
    getPendingFiatWithdrawals: builder.query<
      PendingFiatWithdrawOrderDto[],
      void
    >({
      queryFn: () =>
        handleErrors(() =>
          withdrawFiatClient.getPendingFiatWithdrawOrderDetails(),
        ),
      providesTags: [{ type: 'PendingFiatWithdrawal' }],
    }),
    getPendingCryptoWithdrawals: builder.query<
      PendingCryptoWithdrawOrderDto[],
      void
    >({
      queryFn: () =>
        handleErrors(() =>
          withdrawCryptoClient.getPendingCryptoWithdrawOrderDetails(),
        ),
      providesTags: [{ type: 'PendingCryptoWithdrawal' }],
    }),
  }),
});

export const {
  useGetPendingFiatWithdrawalsQuery,
  useGetPendingCryptoWithdrawalsQuery,
} = withdrawalsApi;

export const pendingWithdrawalTags = [
  { type: 'PendingFiatWithdrawal' },
  { type: 'PendingCryptoWithdrawal' },
] as const;

/**
 * Fiat and crypto pending withdrawals as one list, with one loading flag.
 * Callers never see a half-loaded list, which is what made the deep-linked
 * pending modal render an empty shell.
 */
export function usePendingWithdrawals() {
  const { data: fiatOrders, isLoading: isLoadingFiat } =
    useGetPendingFiatWithdrawalsQuery();
  const { data: cryptoOrders, isLoading: isLoadingCrypto } =
    useGetPendingCryptoWithdrawalsQuery();

  const isLoading = isLoadingFiat || isLoadingCrypto;

  const pendingWithdrawals = useMemo(() => {
    if (isLoading) {
      return [];
    }

    const combined = [
      ...(fiatOrders ?? []).map(order => toPendingWithdrawal(order, false)),
      ...(cryptoOrders ?? []).map(order => toPendingWithdrawal(order, true)),
    ];

    // One row per product — the two endpoints can both report the same code.
    return combined.filter(
      (order, i) =>
        combined.findIndex(o =>
          isSameProduct(o.productCode, order.productCode),
        ) === i,
    );
  }, [fiatOrders, cryptoOrders, isLoading]);

  return { pendingWithdrawals, isLoading };
}

const isSameProduct = (a?: string, b?: string) =>
  !!a && !!b && a.toUpperCase() === b.toUpperCase();

/** Case-insensitive lookup — product codes are not cased consistently. */
export const findPendingWithdrawal = (
  pendingWithdrawals: PendingWithdrawal[],
  productCode?: string,
) =>
  pendingWithdrawals.find(order =>
    isSameProduct(order.productCode, productCode),
  );

export const hasPendingWithdrawal = (
  pendingWithdrawals: PendingWithdrawal[],
  productCode?: string,
) => findPendingWithdrawal(pendingWithdrawals, productCode) != null;
